<template>
  <div class="admin-login-container">
    <div class="login-card">
      <div class="header">
        <h2>{{ getLang == 'indo' ? 'Admin Crypto Verifikasi' : 'Crypto Admin Panel' }}</h2>
        <p>{{ getLang == 'indo' ? 'Akses Terbatas' : 'Restricted Access' }}</p>
      </div>

      <!-- Lockout Warning -->
      <div v-if="lockoutTimeRemaining > 0" class="alert alert-danger">
        <strong>{{ getLang == 'indo' ? 'Akun Terkunci' : 'Account Locked' }}</strong><br>
        {{ getLang == 'indo' ? 'Coba lagi dalam' : 'Try again in' }} {{ lockoutTimeRemaining }} {{ getLang == 'indo' ? 'detik' : 'seconds' }}
      </div>

      <!-- Failed Attempts Warning -->
      <div v-if="failedAttempts > 0 && lockoutTimeRemaining === 0" class="alert alert-warning">
        {{ getLang == 'indo' ? 'Percobaan gagal' : 'Failed attempts' }}: {{ failedAttempts }}/3
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="username">{{ getLang == 'indo' ? 'Username Admin' : 'Admin Username' }}</label>
          <input
            type="text"
            id="username"
            v-model="credentials.username"
            :disabled="lockoutTimeRemaining > 0"
            required
            maxlength="50"
            autocomplete="username"
            class="form-control"
          >
        </div>

        <div class="form-group">
          <label for="password">{{ getLang == 'indo' ? 'Password Admin' : 'Admin Password' }}</label>
          <input
            type="password"
            id="password"
            v-model="credentials.password"
            :disabled="lockoutTimeRemaining > 0"
            required
            maxlength="100"
            autocomplete="current-password"
            class="form-control"
          >
        </div>

        <button 
          type="submit" 
          :disabled="lockoutTimeRemaining > 0 || loading"
          class="login-btn"
        >
          <span v-if="loading">{{ getLang == 'indo' ? 'Memverifikasi...' : 'Verifying...' }}</span>
          <span v-else>{{ getLang == 'indo' ? 'Masuk Admin' : 'Admin Login' }}</span>
        </button>
      </form>

      <div class="security-info">
        <small>
          <i class="security-icon">🔐</i>
          {{ getLang == 'indo' ? 'Semua aktivitas dipantau dan dicatat' : 'All activities are monitored and logged' }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import adminAuth from '../services/adminAuth';
import Swal from 'sweetalert2';

export default {
  name: 'AdminLogin',
  data() {
    return {
      credentials: {
        username: '',
        password: ''
      },
      loading: false,
      lockoutTimeRemaining: 0,
      failedAttempts: 0,
      getLang: localStorage.getItem('setLang') || 'uk'
    };
  },
  mounted() {
    // Set security headers
    this.setSecurityHeaders();
    
    // Check lockout status
    this.checkLockoutStatus();
    
    // Update lockout timer
    this.lockoutTimer = setInterval(() => {
      if (this.lockoutTimeRemaining > 0) {
        this.lockoutTimeRemaining--;
      } else {
        clearInterval(this.lockoutTimer);
      }
    }, 1000);

    // Check if already authenticated
    if (adminAuth.isAuthenticated()) {
      this.$router.push('/admin/dashboard');
    }
  },
  beforeDestroy() {
    if (this.lockoutTimer) {
      clearInterval(this.lockoutTimer);
    }
  },
  methods: {
    setSecurityHeaders() {
      // Add security meta tags
      const metaTags = [
        { name: 'referrer', content: 'no-referrer' },
        { 'http-equiv': 'X-Content-Type-Options', content: 'nosniff' },
        { 'http-equiv': 'X-Frame-Options', content: 'DENY' },
        { 'http-equiv': 'X-XSS-Protection', content: '1; mode=block' }
      ];

      metaTags.forEach(tag => {
        const meta = document.createElement('meta');
        Object.keys(tag).forEach(key => {
          meta.setAttribute(key, tag[key]);
        });
        document.head.appendChild(meta);
      });
    },

    checkLockoutStatus() {
      this.lockoutTimeRemaining = adminAuth.getLockoutTimeRemaining();
      
      const attempts = localStorage.getItem('admin_attempts');
      this.failedAttempts = attempts ? parseInt(attempts) : 0;
    },

    async handleLogin() {
      if (this.lockoutTimeRemaining > 0) return;

      this.loading = true;

      try {
        // Input validation
        if (!this.credentials.username.trim() || !this.credentials.password.trim()) {
          throw new Error(this.getLang === 'indo' ? 'Username dan password harus diisi' : 'Username and password are required');
        }

        // Validate credentials
        adminAuth.validateCredentials(this.credentials.username, this.credentials.password);
        
        // Create session
        adminAuth.createSession();

        // Success message
        await Swal.fire({
          icon: 'success',
          title: this.getLang === 'indo' ? 'Berhasil Masuk' : 'Login Successful',
          text: this.getLang === 'indo' ? 'Selamat datang, Admin!' : 'Welcome, Admin!',
          timer: 1500,
          showConfirmButton: false
        });

        // Redirect to dashboard
        this.$router.push('/admin/dashboard');

      } catch (error) {
        console.error('Login error:', error);
        
        this.checkLockoutStatus(); // Update UI
        
        await Swal.fire({
          icon: 'error',
          title: this.getLang === 'indo' ? 'Login Gagal' : 'Login Failed',
          text: error.message,
          confirmButtonText: 'OK'
        });

        // Clear password
        this.credentials.password = '';
        
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.admin-login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h2 {
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
}

.header p {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.alert {
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.alert-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s, box-shadow 0.3s;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.login-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.security-info {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e1e5e9;
}

.security-info small {
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.security-icon {
  font-size: 16px;
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .login-card {
    padding: 30px 20px;
    margin: 10px;
  }
  
  .header h2 {
    font-size: 24px;
  }
}
</style>